/**
 * Email templates for various campaigns
 */

/**
 * Generate a standard newsletter template
 */
export const standardNewsletter = (
  title: string,
  content: string,
  callToAction?: { text: string; url: string }
) => {
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Inter', Helvetica, Arial, sans-serif; background-color: #0a0a0a; color: #ffffff; line-height: 1.6;">
      <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #0a0a0a; padding: 20px;">
        <tr>
          <td align="center" style="padding: 20px 0;">
            <div style="max-width: 600px; margin: 0 auto; background-color: #1a1a1a; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);">
              <!-- Header -->
              <div style="background: linear-gradient(90deg, #000000, #1a1a1a); border-bottom: 1px solid rgba(147, 51, 234, 0.3); padding: 30px 20px; text-align: center;">
                <img src="https://grailed804.com/images/grailed804logo.webp" alt="GRAILED804" style="max-width: 200px; height: auto; margin: 0 auto;">
              </div>
              
              <!-- Content -->
              <div style="padding: 40px 30px; background-color: #1a1a1a;">
                <h1 style="color: #9333ea; margin-top: 0; margin-bottom: 20px; font-size: 28px; font-weight: 700; text-align: center;">${title}</h1>
                
                <div style="color: #ffffff; font-size: 16px;">
                  ${content}
                </div>
                
                ${callToAction ? `
                <!-- Call to Action Button -->
                <div style="text-align: center; margin: 35px 0;">
                  <a href="${callToAction.url}" style="background-color: #9333ea; color: white; padding: 14px 30px; text-decoration: none; border-radius: 6px; font-weight: 600; display: inline-block; font-size: 16px; transition: background-color 0.3s ease;">
                    ${callToAction.text}
                  </a>
                </div>
                ` : ''}
              </div>
              
              <!-- Footer -->
              <div style="background-color: #0a0a0a; padding: 25px 20px; text-align: center; font-size: 14px; color: #888888; border-top: 1px solid rgba(147, 51, 234, 0.3);">
                <p style="margin-top: 0; margin-bottom: 10px;">&copy; ${new Date().getFullYear()} GRAILED804. All rights reserved.</p>
                <div style="margin-bottom: 15px;">
                  <a href="https://grailed804.com/terms" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Terms</a>
                  <a href="https://grailed804.com/privacy" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Privacy</a>
                  <a href="https://grailed804.com/unsubscribe" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Unsubscribe</a>
                </div>
                <p style="margin: 0; color: #888888; font-size: 12px;">This email was sent to you because you subscribed to GRAILED804 newsletters.</p>
              </div>
            </div>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};

/**
 * Generate a test newsletter template
 */
export const testNewsletter = () => {
  return standardNewsletter(
    "Test Newsletter",
    `
      <p style="margin-bottom: 20px;">This is a test newsletter from GRAILED804. We're excited to launch our new newsletter feature!</p>
      
      <p style="margin-bottom: 15px;">This is just a template to test our newsletter functionality. Here's what you can expect from our actual newsletters:</p>
      <ul style="padding-left: 25px; margin-bottom: 25px; color: #ffffff;">
        <li style="margin-bottom: 8px;">New product announcements and drops</li>
        <li style="margin-bottom: 8px;">Exclusive deals and promotions for subscribers</li>
        <li style="margin-bottom: 8px;">Sneaker industry news and trend updates</li>
        <li style="margin-bottom: 8px;">Special offers and early access opportunities</li>
      </ul>
      
      <p style="margin-bottom: 0; font-weight: 500; color: #9333ea;">Thank you for subscribing to GRAILED804 newsletters!</p>
    `,
    { text: "Visit Our Store", url: "https://grailed804.com/shop" }
  );
};

/**
 * Generate a launch announcement template
 */
export const launchAnnouncement = (
  launchDate: string, // e.g., "June 15, 2023"
  launchUrl: string
) => {
  return standardNewsletter(
    "We're Launching Soon!",
    `
      <p style="margin-bottom: 20px; font-size: 18px;">We're excited to announce that GRAILED804 will be officially launching on <strong style="color: #9333ea;">${launchDate}</strong>!</p>
      
      <p style="margin-bottom: 25px;">As one of our early subscribers, you'll be the first to know when our site goes live, 
      and you'll have access to exclusive launch day offers and promotions.</p>
      
      <div style="background-color: #0a0a0a; border-left: 4px solid #9333ea; padding: 15px 20px; margin-bottom: 25px; border-radius: 0 4px 4px 0;">
        <p style="margin-top: 0; margin-bottom: 15px; font-weight: 600; color: #ffffff;">What to expect:</p>
        <ul style="padding-left: 20px; margin-bottom: 0; color: #ffffff;">
          <li style="margin-bottom: 8px;">Premium, authenticated sneakers from top brands</li>
          <li style="margin-bottom: 8px;">Exclusive collections you won't find elsewhere</li>
          <li style="margin-bottom: 8px;">Special discounts for our email subscribers</li>
          <li style="margin-bottom: 0;">Seamless shopping experience</li>
        </ul>
      </div>
      
      <p style="margin-bottom: 0; text-align: center; font-weight: 500; color: #9333ea;">Mark your calendar and get ready for the ultimate sneaker shopping experience!</p>
    `,
    { text: "Visit Our Site", url: launchUrl }
  );
};

/**
 * Generate a new product drop announcement
 */
export const newProductDrop = (
  productName: string,
  productDescription: string,
  productImageUrl: string,
  productUrl: string
) => {
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>New Drop: ${productName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Inter', Helvetica, Arial, sans-serif; background-color: #0a0a0a; color: #ffffff; line-height: 1.6;">
      <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #0a0a0a; padding: 20px;">
        <tr>
          <td align="center" style="padding: 20px 0;">
            <div style="max-width: 600px; margin: 0 auto; background-color: #1a1a1a; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);">
              <!-- Header -->
              <div style="background: linear-gradient(90deg, #000000, #1a1a1a); border-bottom: 1px solid rgba(147, 51, 234, 0.3); padding: 30px 20px; text-align: center;">
                <img src="https://grailed804.com/images/grailed804logo.webp" alt="GRAILED804" style="max-width: 200px; height: auto; margin: 0 auto;">
              </div>
              
              <!-- Content -->
              <div style="padding: 40px 30px; background-color: #1a1a1a;">
                <h1 style="color: #9333ea; margin-top: 0; margin-bottom: 25px; font-size: 28px; font-weight: 700; text-align: center;">New Drop Alert 🔥</h1>
                
                <p style="font-size: 16px; color: #ffffff; text-align: center; margin-bottom: 30px;">We just added an incredible new product to our collection:</p>
                
                <div style="background-color: #0a0a0a; border-radius: 8px; padding: 25px; margin-bottom: 30px; border: 1px solid rgba(147, 51, 234, 0.2);">
                  <h2 style="color: #ffffff; font-size: 22px; margin-top: 0; margin-bottom: 20px; text-align: center; font-weight: 700;">${productName}</h2>
                  <div style="text-align: center; margin-bottom: 20px;">
                    <img src="${productImageUrl}" alt="${productName}" style="max-width: 100%; height: auto; border-radius: 6px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);">
                  </div>
                  <p style="font-style: italic; color: #ffffff; text-align: center; margin-bottom: 0;">${productDescription}</p>
                </div>
                
                <p style="font-weight: 600; text-align: center; color: #ffffff; margin-bottom: 30px;">Don't miss out on this exclusive drop. Limited quantities available!</p>
                
                <!-- Call to Action Button -->
                <div style="text-align: center; margin-bottom: 10px;">
                  <a href="${productUrl}" style="background-color: #9333ea; color: white; padding: 14px 30px; text-decoration: none; border-radius: 6px; font-weight: 600; display: inline-block; font-size: 16px; transition: background-color 0.3s ease; border: none;">
                    Shop Now
                  </a>
                </div>
              </div>
              
              <!-- Footer -->
              <div style="background-color: #0a0a0a; padding: 25px 20px; text-align: center; font-size: 14px; color: #888888; border-top: 1px solid rgba(147, 51, 234, 0.3);">
                <p style="margin-top: 0; margin-bottom: 10px;">&copy; ${new Date().getFullYear()} GRAILED804. All rights reserved.</p>
                <div style="margin-bottom: 15px;">
                  <a href="https://grailed804.com/terms" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Terms</a>
                  <a href="https://grailed804.com/privacy" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Privacy</a>
                  <a href="https://grailed804.com/unsubscribe" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Unsubscribe</a>
                </div>
                <p style="margin: 0; color: #888888; font-size: 12px;">This email was sent to you because you subscribed to GRAILED804 newsletters.</p>
              </div>
            </div>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};

/**
 * Generate a featured collection email template showcasing multiple products in a grid layout
 */
export const featuredCollection = (
  collectionTitle: string,
  collectionDescription: string,
  products: Array<{
    name: string;
    price: number;
    imageUrl: string;
    productUrl: string;
    brand?: string;
    condition?: string;
  }>,
  allCollectionUrl: string
) => {
  // Generate product grid HTML
  const productsHtml = products.map(product => `
    <div style="width: 100%; margin-bottom: 25px;">
      <div style="background-color: #0a0a0a; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2); border: 1px solid rgba(147, 51, 234, 0.1);">
        <div style="position: relative;">
          <img src="${product.imageUrl}" alt="${product.name}" style="width: 100%; height: 180px; object-fit: contain; background-color: #000000; padding: 10px;">
          ${product.brand ? `
            <div style="position: absolute; top: 10px; left: 10px; background-color: rgba(0, 0, 0, 0.7); padding: 4px 8px; border-radius: 4px; font-size: 11px; color: #ffffff;">
              ${product.brand}
            </div>
          ` : ''}
          ${product.condition ? `
            <div style="position: absolute; top: 10px; right: 10px; background-color: rgba(147, 51, 234, 0.7); padding: 4px 8px; border-radius: 4px; font-size: 11px; color: #ffffff;">
              ${product.condition}
            </div>
          ` : ''}
        </div>
        <div style="padding: 15px;">
          <h3 style="color: #ffffff; font-size: 14px; margin-top: 0; margin-bottom: 8px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${product.name}
          </h3>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: #9333ea; font-weight: 700; font-size: 16px;">$${product.price.toLocaleString()}</span>
            <a href="${product.productUrl}" style="background-color: #0a0a0a; border: 1px solid #9333ea; color: #9333ea; padding: 5px 10px; border-radius: 4px; text-decoration: none; font-size: 12px; font-weight: 500; transition: all 0.2s ease;">View</a>
          </div>
        </div>
      </div>
    </div>
  `).join('');

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${collectionTitle} - GRAILED804</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Inter', Helvetica, Arial, sans-serif; background-color: #0a0a0a; color: #ffffff; line-height: 1.6;">
      <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #0a0a0a; padding: 20px;">
        <tr>
          <td align="center" style="padding: 20px 0;">
            <div style="max-width: 600px; margin: 0 auto; background-color: #1a1a1a; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);">
              <!-- Header with special gradient background -->
              <div style="background: linear-gradient(135deg, #000000, #1a1a1a); border-bottom: 1px solid rgba(147, 51, 234, 0.3); padding: 30px 20px; text-align: center; position: relative; overflow: hidden;">
                <!-- Decorative purple blur effect -->
                <div style="position: absolute; width: 150px; height: 150px; border-radius: 75px; background: rgba(147, 51, 234, 0.15); filter: blur(40px); top: -50px; right: -50px; z-index: 1;"></div>
                <div style="position: absolute; width: 100px; height: 100px; border-radius: 50px; background: rgba(147, 51, 234, 0.1); filter: blur(30px); bottom: -30px; left: -30px; z-index: 1;"></div>
                
                <div style="position: relative; z-index: 2;">
                  <img src="https://grailed804.com/images/grailed804logo.webp" alt="GRAILED804" style="max-width: 200px; height: auto; margin: 0 auto;">
                </div>
              </div>
              
              <!-- Hero section -->
              <div style="background-color: #0a0a0a; padding: 40px 30px; text-align: center; position: relative; overflow: hidden; border-bottom: 1px solid rgba(147, 51, 234, 0.2);">
                <!-- Decorative elements -->
                <div style="position: absolute; width: 200px; height: 200px; border-radius: 100px; background: rgba(147, 51, 234, 0.05); filter: blur(60px); top: -100px; right: -100px;"></div>
                
                <h1 style="color: #ffffff; margin-top: 0; margin-bottom: 15px; font-size: 28px; font-weight: 700; text-align: center; position: relative; z-index: 2;">
                  <span style="color: #9333ea;">${collectionTitle}</span>
                </h1>
                
                <div style="width: 40px; height: 2px; background-color: #9333ea; margin: 0 auto 20px;"></div>
                
                <p style="color: #cccccc; font-size: 16px; max-width: 450px; margin: 0 auto 25px; position: relative; z-index: 2;">
                  ${collectionDescription}
                </p>
              </div>
              
              <!-- Content -->
              <div style="padding: 40px 30px; background-color: #1a1a1a;">
                <h2 style="color: #ffffff; font-size: 20px; margin-top: 0; margin-bottom: 30px; text-align: left; font-weight: 600; padding-bottom: 10px; border-bottom: 1px solid rgba(147, 51, 234, 0.2);">
                  Featured Products
                </h2>
                
                <!-- Products grid -->
                <div style="display: inline-block; width: 100%; text-align: center;">
                  <!-- Two-column grid for desktop, one column on mobile -->
                  <table style="width: 100%; border-collapse: separate; border-spacing: 10px;">
                    <tr>
                      <td style="width: 50%; vertical-align: top;">
                        ${products.slice(0, Math.ceil(products.length/2)).map((_, index) => index * 2 < products.length ? productsHtml.split('</div>\n  </div>')[index * 2] + '</div>\n  </div>' : '').join('')}
                      </td>
                      <td style="width: 50%; vertical-align: top;">
                        ${products.slice(Math.ceil(products.length/2)).map((_, index) => (index * 2 + 1) < products.length ? productsHtml.split('</div>\n  </div>')[index * 2 + 1] + '</div>\n  </div>' : '').join('')}
                      </td>
                    </tr>
                  </table>
                </div>
                
                <!-- View All CTA -->
                <div style="text-align: center; margin-top: 40px;">
                  <a href="${allCollectionUrl}" style="background-color: #9333ea; color: white; padding: 14px 30px; text-decoration: none; border-radius: 6px; font-weight: 600; display: inline-block; font-size: 16px; transition: background-color 0.3s ease; border: none;">
                    View Entire Collection
                  </a>
                </div>
              </div>
              
              <!-- Benefits section -->
              <div style="background-color: #0a0a0a; padding: 30px; border-top: 1px solid rgba(147, 51, 234, 0.2); border-bottom: 1px solid rgba(147, 51, 234, 0.2);">
                <table style="width: 100%; border-collapse: collapse;">
                  <tr>
                    <td style="width: 33.33%; text-align: center; padding: 15px;">
                      <div style="font-size: 22px; color: #9333ea; margin-bottom: 10px;">✓</div>
                      <h3 style="color: #ffffff; font-size: 14px; margin: 0 0 8px; font-weight: 600;">100% Authentic</h3>
                      <p style="color: #888888; font-size: 12px; margin: 0;">All products verified</p>
                    </td>
                    <td style="width: 33.33%; text-align: center; padding: 15px;">
                      <div style="font-size: 22px; color: #9333ea; margin-bottom: 10px;">🚚</div>
                      <h3 style="color: #ffffff; font-size: 14px; margin: 0 0 8px; font-weight: 600;">Fast Shipping</h3>
                      <p style="color: #888888; font-size: 12px; margin: 0;">Quick & secure delivery</p>
                    </td>
                    <td style="width: 33.33%; text-align: center; padding: 15px;">
                      <div style="font-size: 22px; color: #9333ea; margin-bottom: 10px;">💯</div>
                      <h3 style="color: #ffffff; font-size: 14px; margin: 0 0 8px; font-weight: 600;">Premium Quality</h3>
                      <p style="color: #888888; font-size: 12px; margin: 0;">Best in the market</p>
                    </td>
                  </tr>
                </table>
              </div>
              
              <!-- Footer -->
              <div style="background-color: #0a0a0a; padding: 25px 20px; text-align: center; font-size: 14px; color: #888888; border-top: 1px solid rgba(147, 51, 234, 0.3);">
                <p style="margin-top: 0; margin-bottom: 10px;">&copy; ${new Date().getFullYear()} GRAILED804. All rights reserved.</p>
                <div style="margin-bottom: 15px;">
                  <a href="https://grailed804.com/terms" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Terms</a>
                  <a href="https://grailed804.com/privacy" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Privacy</a>
                  <a href="https://grailed804.com/unsubscribe" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Unsubscribe</a>
                </div>
                <p style="margin: 0; color: #888888; font-size: 12px;">This email was sent to you because you subscribed to GRAILED804 newsletters.</p>
                <div style="margin-top: 15px;">
                  <a href="https://instagram.com/grailed804" style="color: #9333ea; text-decoration: none; margin: 0 6px;">Instagram</a>
                  <a href="https://twitter.com/grailed804" style="color: #9333ea; text-decoration: none; margin: 0 6px;">Twitter</a>
                  <a href="https://facebook.com/grailed804" style="color: #9333ea; text-decoration: none; margin: 0 6px;">Facebook</a>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};

/**
 * Generate a promotional sale template with countdown timer effect
 */
export const promotionalSale = (
  saleTitle: string,
  saleDescription: string,
  discountCode: string, 
  endDate: string, // Format: "December 31, 2023"
  storeUrl: string,
  heroImageUrl?: string
) => {
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${saleTitle} - GRAILED804</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Inter', Helvetica, Arial, sans-serif; background-color: #0a0a0a; color: #ffffff; line-height: 1.6;">
      <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #0a0a0a; padding: 20px;">
        <tr>
          <td align="center" style="padding: 20px 0;">
            <div style="max-width: 600px; margin: 0 auto; background-color: #1a1a1a; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);">
              <!-- Header -->
              <div style="background: linear-gradient(90deg, #000000, #1a1a1a); border-bottom: 1px solid rgba(147, 51, 234, 0.3); padding: 30px 20px; text-align: center;">
                <img src="https://grailed804.com/images/grailed804logo.webp" alt="GRAILED804" style="max-width: 200px; height: auto; margin: 0 auto;">
              </div>
              
              <!-- Hero Banner with Discount -->
              <div style="background-color: #0a0a0a; position: relative; overflow: hidden; ${heroImageUrl ? `
                background-image: url(${heroImageUrl});
                background-size: cover;
                background-position: center;
                min-height: 200px;
              ` : 'padding: 60px 20px;'}">
                <!-- Dark overlay for readability if image exists -->
                ${heroImageUrl ? `<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.7);"></div>` : ''}
                
                <div style="position: relative; padding: ${heroImageUrl ? '60px 20px' : '0'}; text-align: center; z-index: 2;">
                  <h1 style="color: #ffffff; margin-top: 0; margin-bottom: 15px; font-size: 32px; font-weight: 800; text-transform: uppercase; text-shadow: 0 2px 4px rgba(0,0,0,0.3);">
                    ${saleTitle}
                  </h1>
                  
                  <div style="width: 60px; height: 3px; background-color: #9333ea; margin: 0 auto 20px;"></div>
                  
                  <p style="color: #ffffff; font-size: 18px; max-width: 450px; margin: 0 auto 25px; position: relative; z-index: 2; text-shadow: 0 1px 2px rgba(0,0,0,0.3);">
                    ${saleDescription}
                  </p>
                  
                  <!-- Discount code highlight -->
                  <div style="background-color: #9333ea; display: inline-block; padding: 12px 25px; border-radius: 6px; margin-bottom: 20px; box-shadow: 0 4px 15px rgba(147, 51, 234, 0.3);">
                    <p style="margin: 0 0 5px; font-size: 14px; text-transform: uppercase; color: rgba(255,255,255,0.8);">Use Code</p>
                    <p style="margin: 0; font-size: 26px; font-weight: 700; letter-spacing: 2px; color: #ffffff;">${discountCode}</p>
                  </div>
                </div>
              </div>
              
              <!-- Countdown Section -->
              <div style="background-color: #0a0a0a; padding: 30px 20px; text-align: center; border-top: 1px solid rgba(147, 51, 234, 0.2); border-bottom: 1px solid rgba(147, 51, 234, 0.2);">
                <p style="margin: 0 0 15px; text-transform: uppercase; font-weight: 600; color: #9333ea; letter-spacing: 1px; font-size: 14px;">LIMITED TIME OFFER</p>
                
                <p style="margin: 0 0 20px; font-size: 16px; color: #ffffff;">Sale ends on ${endDate}</p>
                
                <!-- Countdown timer look -->
                <table style="margin: 0 auto;">
                  <tr>
                    <td style="padding: 0 15px; text-align: center;">
                      <div style="background-color: #1a1a1a; border: 1px solid rgba(147, 51, 234, 0.3); border-radius: 8px; padding: 15px 10px; width: 60px;">
                        <p style="margin: 0; font-size: 22px; font-weight: 700; color: #ffffff;">00</p>
                        <p style="margin: 5px 0 0; font-size: 12px; color: #9333ea;">DAYS</p>
                      </div>
                    </td>
                    <td style="padding: 0 15px; text-align: center;">
                      <div style="background-color: #1a1a1a; border: 1px solid rgba(147, 51, 234, 0.3); border-radius: 8px; padding: 15px 10px; width: 60px;">
                        <p style="margin: 0; font-size: 22px; font-weight: 700; color: #ffffff;">00</p>
                        <p style="margin: 5px 0 0; font-size: 12px; color: #9333ea;">HOURS</p>
                      </div>
                    </td>
                    <td style="padding: 0 15px; text-align: center;">
                      <div style="background-color: #1a1a1a; border: 1px solid rgba(147, 51, 234, 0.3); border-radius: 8px; padding: 15px 10px; width: 60px;">
                        <p style="margin: 0; font-size: 22px; font-weight: 700; color: #ffffff;">00</p>
                        <p style="margin: 5px 0 0; font-size: 12px; color: #9333ea;">MINS</p>
                      </div>
                    </td>
                    <td style="padding: 0 15px; text-align: center;">
                      <div style="background-color: #1a1a1a; border: 1px solid rgba(147, 51, 234, 0.3); border-radius: 8px; padding: 15px 10px; width: 60px;">
                        <p style="margin: 0; font-size: 22px; font-weight: 700; color: #ffffff;">00</p>
                        <p style="margin: 5px 0 0; font-size: 12px; color: #9333ea;">SECS</p>
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
              
              <!-- Content -->
              <div style="padding: 40px 30px; background-color: #1a1a1a; text-align: center;">
                <h2 style="color: #ffffff; font-size: 22px; margin-top: 0; margin-bottom: 20px; text-align: center; font-weight: 600;">
                  Don't Miss Out!
                </h2>
                
                <p style="color: #cccccc; font-size: 16px; max-width: 450px; margin: 0 auto 30px;">
                  Shop now to take advantage of this exclusive offer before it expires.
                </p>
                
                <!-- CTA Button -->
                <div style="margin-bottom: 10px;">
                  <a href="${storeUrl}" style="background-color: #9333ea; color: white; padding: 16px 35px; text-decoration: none; border-radius: 6px; font-weight: 600; display: inline-block; font-size: 16px; transition: background-color 0.3s ease; border: none; text-transform: uppercase; letter-spacing: 1px;">
                    Shop Now
                  </a>
                </div>
                
                <p style="color: #888888; font-size: 13px; margin-top: 20px;">
                  *Terms and conditions apply. Discount valid for a limited time only.
                </p>
              </div>
              
              <!-- Footer -->
              <div style="background-color: #0a0a0a; padding: 25px 20px; text-align: center; font-size: 14px; color: #888888; border-top: 1px solid rgba(147, 51, 234, 0.3);">
                <p style="margin-top: 0; margin-bottom: 10px;">&copy; ${new Date().getFullYear()} GRAILED804. All rights reserved.</p>
                <div style="margin-bottom: 15px;">
                  <a href="https://grailed804.com/terms" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Terms</a>
                  <a href="https://grailed804.com/privacy" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Privacy</a>
                  <a href="https://grailed804.com/unsubscribe" style="color: #9333ea; text-decoration: none; margin: 0 10px; font-weight: 500;">Unsubscribe</a>
                </div>
                <p style="margin: 0; color: #888888; font-size: 12px;">This email was sent to you because you subscribed to GRAILED804 newsletters.</p>
              </div>
            </div>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
}; 